import { Chart, registerables } from 'chart.js';
import 'chartjs-adapter-date-fns';
import React, { useEffect, useRef, useState } from 'react';
import { supabaseClient } from '../../services/supabase';
import { supabaseLocationService } from '../../services/supabaseLocationService';
import './EMFACFleetComparisonChart.css';

Chart.register(...registerables);

interface EMFACFleetComparisonChartProps {
  location: string;
  geofips: number | null;
}

interface VMTDataPoint {
  year: number;
  emfac2021Fleet_ldv: number;
  emfac2025Fleet_ldv: number;
}

export const EMFACFleetComparisonChart: React.FC<EMFACFleetComparisonChartProps> = ({ location, geofips }) => {
  const chartRef = useRef<HTMLCanvasElement>(null);
  const chartInstance = useRef<Chart | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<VMTDataPoint[]>([]);
  const [locationName, setLocationName] = useState<string>('');

  useEffect(() => {
    const fetchData = async () => {
      if (!location || !geofips) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        // Get location info to determine if it's a city or county
        const { data: locationData, error: locationError } = await supabaseClient
          .from('population')
          .select('location_name, geography_type, county')
          .eq('geofips', geofips)
          .limit(1)
          .single();

        if (locationError || !locationData) {
          throw new Error('Could not find location data');
        }

        setLocationName(locationData.location_name);

        console.log('🔍 EMFAC Fleet VMT: Fetching data for geofips:', geofips, locationData.location_name, locationData.geography_type);

        // Fetch EMFAC2021 Fleet data (emfac_fleet_mat) - LDV only, City level
        // Try wrapper view first, fall back to materialized view
        let emfac2021FleetData: any[] = [];
        let emfac2021FleetError: any = null;
        
        // Try emfac_fleet wrapper view first
        const { data: emfac2021FleetDataWrapper, error: emfac2021FleetErrorWrapper } = await supabaseClient
          .from('emfac_fleet')
          .select('year, class, total_vmt')
          .eq('geofips', geofips)
          .eq('geography_type', 'City')
          .eq('class', 'LDV')
          .gte('year', 2015)
          .lte('year', 2023)
          .order('year', { ascending: true });

        if (!emfac2021FleetErrorWrapper && emfac2021FleetDataWrapper) {
          emfac2021FleetData = emfac2021FleetDataWrapper;
        } else {
          // Fall back to materialized view
          const { data: emfac2021FleetDataMat, error: emfac2021FleetErrorMat } = await supabaseClient
            .from('emfac_fleet_mat')
            .select('year, class, total_vmt')
            .eq('geofips', geofips)
            .eq('geography_type', 'City')
            .eq('class', 'LDV')
            .gte('year', 2015)
            .lte('year', 2023)
            .order('year', { ascending: true });
          
          if (!emfac2021FleetErrorMat && emfac2021FleetDataMat) {
            emfac2021FleetData = emfac2021FleetDataMat;
          } else {
            emfac2021FleetError = emfac2021FleetErrorMat || emfac2021FleetErrorWrapper;
          }
        }

        if (emfac2021FleetError) {
          console.error('Error fetching EMFAC2021 Fleet data:', emfac2021FleetError);
          // Don't throw - might not have data for this location
        }

        // Fetch EMFAC2025 Fleet data (emfac2025_fleet_mat) - LDV only, City level
        // Try wrapper view first, fall back to materialized view
        let emfac2025FleetData: any[] = [];
        let emfac2025FleetError: any = null;
        
        // Try emfac2025_fleet wrapper view first
        const { data: emfac2025FleetDataWrapper, error: emfac2025FleetErrorWrapper } = await supabaseClient
          .from('emfac2025_fleet')
          .select('year, class, total_vmt')
          .eq('geofips', geofips)
          .eq('geography_type', 'City')
          .eq('class', 'LDV')
          .gte('year', 2015)
          .lte('year', 2023)
          .order('year', { ascending: true });

        if (!emfac2025FleetErrorWrapper && emfac2025FleetDataWrapper) {
          emfac2025FleetData = emfac2025FleetDataWrapper;
        } else {
          // Fall back to materialized view
          const { data: emfac2025FleetDataMat, error: emfac2025FleetErrorMat } = await supabaseClient
            .from('emfac2025_fleet_mat')
            .select('year, class, total_vmt')
            .eq('geofips', geofips)
            .eq('geography_type', 'City')
            .eq('class', 'LDV')
            .gte('year', 2015)
            .lte('year', 2023)
            .order('year', { ascending: true });
          
          if (!emfac2025FleetErrorMat && emfac2025FleetDataMat) {
            emfac2025FleetData = emfac2025FleetDataMat;
          } else {
            emfac2025FleetError = emfac2025FleetErrorMat || emfac2025FleetErrorWrapper;
          }
        }

        if (emfac2025FleetError) {
          console.error('Error fetching EMFAC2025 Fleet data:', emfac2025FleetError);
          // Don't throw - might not have data for this location
        }

        console.log('📊 EMFAC Fleet VMT: EMFAC2021 Fleet records:', emfac2021FleetData?.length || 0);
        console.log('📊 EMFAC Fleet VMT: EMFAC2025 Fleet records:', emfac2025FleetData?.length || 0);

        // Aggregate data by year
        const yearMap = new Map<number, VMTDataPoint>();

        // Process EMFAC2021 Fleet data (LDV only)
        (emfac2021FleetData || []).forEach(row => {
          const year = row.year;
          const vmt = row.total_vmt || 0;

          if (!yearMap.has(year)) {
            yearMap.set(year, {
              year,
              emfac2021Fleet_ldv: 0,
              emfac2025Fleet_ldv: 0
            });
          }

          const point = yearMap.get(year)!;
          point.emfac2021Fleet_ldv += vmt;
        });

        // Process EMFAC2025 Fleet data (LDV only)
        (emfac2025FleetData || []).forEach(row => {
          const year = row.year;
          const vmt = row.total_vmt || 0;

          if (!yearMap.has(year)) {
            yearMap.set(year, {
              year,
              emfac2021Fleet_ldv: 0,
              emfac2025Fleet_ldv: 0
            });
          }

          const point = yearMap.get(year)!;
          point.emfac2025Fleet_ldv += vmt;
        });

        // Convert to array and sort by year
        const processedData = Array.from(yearMap.values())
          .sort((a, b) => a.year - b.year);

        console.log('📊 EMFAC Fleet VMT: Processed data points:', processedData.length);
        console.log('📊 EMFAC Fleet VMT: Sample data:', processedData.slice(0, 3));

        setData(processedData);
        setLoading(false);
      } catch (err: any) {
        console.error('Error fetching EMFAC Fleet VMT data:', err);
        setError(err.message || 'Failed to load EMFAC Fleet VMT data');
        setLoading(false);
      }
    };

    fetchData();
  }, [location, geofips]);

  useEffect(() => {
    if (data.length === 0 || !chartRef.current) return;

    const ctx = chartRef.current.getContext('2d');
    if (!ctx) return;

    // Destroy existing chart
    if (chartInstance.current) {
      chartInstance.current.destroy();
    }

    // Create new chart
    chartInstance.current = new Chart(ctx, {
      type: 'line',
      data: {
        labels: data.map(d => d.year.toString()),
        datasets: [
          {
            label: 'EMFAC2021 Fleet LDV',
            data: data.map(d => d.emfac2021Fleet_ldv),
            borderColor: 'rgb(156, 163, 175)',
            backgroundColor: 'rgba(156, 163, 175, 0.1)',
            fill: false,
            tension: 0.4,
            borderWidth: 2,
            pointRadius: 3,
            pointHoverRadius: 5,
            borderDash: [5, 5],
            order: 2
          },
          {
            label: 'EMFAC2025 Fleet LDV',
            data: data.map(d => d.emfac2025Fleet_ldv),
            borderColor: 'rgb(59, 130, 246)',
            backgroundColor: 'rgba(59, 130, 246, 0.1)',
            fill: false,
            tension: 0.4,
            borderWidth: 2,
            pointRadius: 4,
            pointHoverRadius: 6,
            order: 1
          }
        ]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: true,
            position: 'top'
          },
          title: {
            display: true,
            text: `Fleet-Level LDV VMT: ${locationName} (2015-2023)`
          },
          tooltip: {
            callbacks: {
              label: function(context) {
                const value = context.parsed.y;
                return `${context.dataset.label}: ${(value ?? 0).toLocaleString()} miles`;
              }
            }
          }
        },
        scales: {
          x: {
            title: {
              display: true,
              text: 'Year'
            }
          },
          y: {
            beginAtZero: true,
            title: {
              display: true,
              text: 'VMT (miles)'
            },
            ticks: {
              callback: function(value) {
                return value.toLocaleString();
              }
            }
          }
        }
      }
    });

    return () => {
      if (chartInstance.current) {
        chartInstance.current.destroy();
      }
    };
  }, [data, locationName]);

  if (loading) {
    return <div className="emfac-fleet-vmt-loading">Loading EMFAC Fleet VMT data...</div>;
  }

  if (error) {
    return <div className="emfac-fleet-vmt-error">{error}</div>;
  }

  if (data.length === 0) {
    return <div className="emfac-fleet-vmt-no-data">No EMFAC Fleet VMT data available for this location</div>;
  }

  return (
    <div className="emfac-fleet-vmt-chart-container">
      <canvas ref={chartRef}></canvas>
    </div>
  );
};

